/**
 * Email Campaign — Sendout Logs.
 *
 * Routed at `/marketing/campaigns/:id/logs` (declared in
 * `pages/marketing/campaigns/campaignRoutes.tsx`; the pre-2026-06-02
 * `/crm/email-campaigns/:id/logs` URL redirects here keeping its id).
 * Reached from the "View Sendout Logs" menu item on the campaigns list
 * (only visible for sent / sending campaigns).
 *
 * Shows per-recipient delivery state for a single campaign:
 *   • Aggregate stats (total / sent / opened / clicked / bounced /
 *     unsubscribed) as a StatsCard row matching the CRM look.
 *   • A filterable, paginated table of recipients with their current
 *     status (pending → sent → opened → clicked, or bounced /
 *     unsubscribed) plus open/click counts.
 *
 * Data: `fetchCampaignRecipients` async thunk in `campaignsSlice` →
 * `GET /campaigns/:id/recipients` on the brain CRM API.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Search, RefreshCw, Mail, MailOpen, MousePointerClick, AlertTriangle, UserMinus, Send, Clock } from "lucide-react";

import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  InputAdornment,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import type { ChipProps } from "@mui/material";
import { PageHeader } from "@/components/ui/page-header";
import { PageLoader } from "@/components/ui/page-loader";
import { Pagination, paginationPropsFromMeta } from "@/components/ui/pagination";

import { withEmailCampaignsAccess } from "@crm-src/components/page-permission-guard";
import { StatsCard } from "@crm-src/components/stats-card";
import { StatsCardsSkeleton } from "@crm-src/components/stats-cards-skeleton";
import { useToast } from "@crm-src/hooks/use-toast";
import { useAppDispatch } from "@crm-src/lib/store";
import {
  fetchCampaignRecipients,
  type CampaignRecipient,
  type CampaignRecipientsResponse,
} from "@crm-src/lib/store/campaignsSlice";
import { BackButton } from "@/components/ui/back-button";

// Per-recipient status → MUI Chip palette + label (inlined during the MUI 5
// migration from the former Tailwind status-class badges).
const STATUS_VARIANTS: Record<CampaignRecipient["status"], { label: string; color: ChipProps["color"] }> = {
  pending: { label: "Pending", color: "warning" },
  sent: { label: "Sent", color: "info" },
  opened: { label: "Opened", color: "success" },
  clicked: { label: "Clicked", color: "success" },
  bounced: { label: "Bounced", color: "error" },
  unsubscribed: { label: "Unsubscribed", color: "default" },
};

const STATUS_FILTERS: Array<{ value: string; label: string }> = [
  { value: "all", label: "All statuses" },
  { value: "pending", label: "Pending" },
  { value: "sent", label: "Sent" },
  { value: "opened", label: "Opened" },
  { value: "clicked", label: "Clicked" },
  { value: "bounced", label: "Bounced" },
  { value: "unsubscribed", label: "Unsubscribed" },
];

function formatTimestamp(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

function EmailCampaignsDetailLogsPage() {
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const { toast } = useToast();

  const [data, setData] = useState<CampaignRecipientsResponse | null>(null);
  const [isLoading, setLoading] = useState<boolean>(true);
  const [isRefreshing, setRefreshing] = useState<boolean>(false);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [search, setSearch] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const perPage = 25;

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(
    async (opts: { silent?: boolean } = {}) => {
      if (!id) return;
      if (opts.silent) setRefreshing(true);
      else setLoading(true);
      try {
        const params: Parameters<typeof fetchCampaignRecipients>[0] = {
          campaignId: id,
          page,
          per_page: perPage,
        };
        if (statusFilter !== "all") params.status = statusFilter;
        if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
        const result = await dispatch(fetchCampaignRecipients(params)).unwrap();
        setData(result);
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to load sendout logs";
        toast({ title: "Could not load logs", description: message, variant: "destructive" });
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [id, page, statusFilter, debouncedSearch, dispatch, toast],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const recipients = data?.recipients ?? [];
  const stats = data?.stats;
  const pagination = data?.pagination;

  const statCards = useMemo(
    () => [
      { title: "Total", value: stats?.total ?? 0, icon: Send, iconColor: "text-blue-600" },
      { title: "Sent", value: stats?.sent ?? 0, icon: Mail, iconColor: "text-blue-600" },
      { title: "Opened", value: stats?.opened ?? 0, icon: MailOpen, iconColor: "text-green-600" },
      { title: "Clicked", value: stats?.clicked ?? 0, icon: MousePointerClick, iconColor: "text-emerald-600" },
      { title: "Bounced", value: stats?.bounced ?? 0, icon: AlertTriangle, iconColor: "text-red-600" },
      { title: "Unsubscribed", value: stats?.unsubscribed ?? 0, icon: UserMinus, iconColor: "text-gray-600" },
    ],
    [stats],
  );

  if (!id) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <PageHeader title="Sendout Logs" />
        <Card>
          <CardContent sx={{ py: 6, textAlign: "center", color: "text.secondary" }}>
            Missing campaign ID in URL.
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <PageHeader
        title="Sendout Logs"
        description="Per-recipient delivery, open, and click state for this campaign."
        actions={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <BackButton label="Back to campaigns" to="/crm/email-campaigns" />
            <Button
              variant="outlined"
              color="inherit"
              size="small"
              onClick={() => void load({ silent: true })}
              disabled={isRefreshing || isLoading}
              startIcon={
                <Box
                  component="span"
                  sx={{
                    display: "inline-flex",
                    "@keyframes spin": { to: { transform: "rotate(360deg)" } },
                    ...(isRefreshing ? { animation: "spin 1s linear infinite" } : {}),
                  }}
                >
                  <RefreshCw size={16} />
                </Box>
              }
            >
              Refresh
            </Button>
          </Box>
        }
      />

      {isLoading && !data ? (
        <StatsCardsSkeleton />
      ) : (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(3, 1fr)", lg: "repeat(6, 1fr)" }, gap: 2 }}>
          {statCards.map((card) => (
            <StatsCard
              key={card.title}
              title={card.title}
              value={card.value.toLocaleString()}
              icon={card.icon}
              iconColor={card.iconColor}
            />
          ))}
        </Box>
      )}

      <Card>
        <CardContent sx={{ pt: 3, display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1.5, alignItems: { sm: "center" }, justifyContent: { sm: "space-between" } }}>
            <TextField
              size="small"
              value={search}
              onChange={(e) => {
                setPage(1);
                setSearch(e.target.value);
              }}
              placeholder="Search by name, email, or company"
              sx={{ width: "100%", maxWidth: { sm: "24rem" } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={16} />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              select
              size="small"
              value={statusFilter}
              onChange={(e) => {
                setPage(1);
                setStatusFilter(e.target.value);
              }}
              sx={{ width: { xs: "100%", sm: 224 } }}
            >
              {STATUS_FILTERS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          {isLoading && !data ? (
            <PageLoader />
          ) : recipients.length === 0 ? (
            <Box sx={{ py: 6, textAlign: "center", color: "text.secondary" }}>
              <Box sx={{ display: "flex", justifyContent: "center", mb: 1, color: "text.disabled" }}><Clock size={32} /></Box>
              No sendout activity yet for this filter.
            </Box>
          ) : (
            <>
              <Box sx={{ borderRadius: 1, border: 1, borderColor: "divider" }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Recipient</TableCell>
                      <TableCell>Company</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Opens</TableCell>
                      <TableCell align="right">Clicks</TableCell>
                      <TableCell>Sent</TableCell>
                      <TableCell>Opened</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recipients.map((r) => {
                      const variant = STATUS_VARIANTS[r.status] ?? STATUS_VARIANTS.pending;
                      return (
                        <TableRow key={r.id}>
                          <TableCell>
                            <Typography sx={{ fontWeight: 500, color: "text.primary" }}>{r.name || "—"}</Typography>
                            <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>{r.email}</Typography>
                          </TableCell>
                          <TableCell sx={{ color: "text.secondary" }}>{r.company || "—"}</TableCell>
                          <TableCell>
                            <Chip size="small" variant="outlined" color={variant.color ?? "default"} label={variant.label} />
                            {r.bounced_at && r.bounce_reason && (
                              <Typography sx={{ mt: 0.5, fontSize: "0.75rem", color: "error.main" }}>{r.bounce_reason}</Typography>
                            )}
                          </TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>{r.open_count}</TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>{r.click_count}</TableCell>
                          <TableCell sx={{ color: "text.secondary", fontSize: "0.875rem" }}>{formatTimestamp(r.sent_at)}</TableCell>
                          <TableCell sx={{ color: "text.secondary", fontSize: "0.875rem" }}>{formatTimestamp(r.opened_at)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>

              <Pagination
                {...paginationPropsFromMeta(pagination)}
                entityNoun="recipients"
                hideIfSinglePage
                onPageChange={setPage}
              />
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

export default withEmailCampaignsAccess(EmailCampaignsDetailLogsPage);
